import { useState } from 'react';
import type { TaskDto } from '@setu/shared';
import AttachField, { type AttachedFile } from './AttachField.tsx';
import AutoTextarea from './AutoTextarea.tsx';

interface Props {
  task: TaskDto;
  onAct: (action: string, extra?: { note?: string; proof?: string; proofFileId?: string }) => Promise<void>;
  /** Errors from the last attempt on this card, shown in place. */
  error?: string | null;
}

/**
 * One piece of work, in whatever state this volunteer has left it.
 *
 * Saying yes is two steps — "I'll do this" then a confirmation — because the
 * prototype testing showed people tapping through the list and finding
 * themselves signed up for things. Saying no is one step, and reversible:
 * the "I can help after all" button exists because the team asked for a
 * button rather than a clickable line of text.
 */
export default function TaskCard({ task, onAct, error }: Props): React.ReactElement {
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [note, setNote] = useState('');
  const [proof, setProof] = useState('');
  const [photo, setPhoto] = useState<AttachedFile | null>(null);
  const [busy, setBusy] = useState(false);

  const act = async (action: string, extra?: { note?: string; proof?: string; proofFileId?: string }) => {
    setBusy(true);
    try {
      await onAct(action, extra);
      setConfirming(false);
      setDeclining(false);
      setFinishing(false);
      setNote('');
      setProof('');
      setPhoto(null);
    } finally {
      setBusy(false);
    }
  };

  const spotsLeft = task.needed > 0 ? Math.max(task.needed - task.acceptedCount, 0) : null;

  return (
    <article className="card stack">
      <div className="row" style={{ gap: 8 }}>
        <span className="pill indigo">{task.areaName}</span>
        <span className="pill">{task.time}</span>
        {task.freq !== 'Once' && <span className="pill">{task.freq.toLowerCase()}</span>}
        {task.dueDate ? (
          <span className="pill marigold">by {formatDate(task.dueDate)}</span>
        ) : (
          <span className="pill">any day</span>
        )}
        {task.myStatus === 'completed' && <span className="pill mint">Done</span>}
        {task.myStatus === 'accepted' && <span className="pill mint">You said yes</span>}
      </div>

      <div>
        <h2>{task.title}</h2>
        {task.why && (
          <p className="soft" style={{ marginTop: 6 }}>
            {task.why}
          </p>
        )}
      </div>

      {task.steps.length > 0 && task.myStatus === 'accepted' && (
        <div className="stack" style={{ gap: 8 }}>
          <span className="kicker">How to do it</span>
          <ol className="stack small soft" style={{ gap: 6, margin: 0, paddingLeft: 20 }}>
            {task.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      <p className="small muted">
        {task.cityName ? `${task.cityName} · ` : ''}
        from {task.createdByName}
        {spotsLeft !== null && task.myStatus !== 'completed'
          ? spotsLeft > 0
            ? ` · ${spotsLeft} still needed`
            : ' · all spots taken'
          : ''}
      </p>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      {/* ------------------------------------------------- not answered yet */}
      {(task.myStatus === null || task.myStatus === 'withdrawn') && !confirming && !declining && (
        <div className="row">
          <button className="btn" onClick={() => setConfirming(true)} disabled={busy}>
            I'll do this
          </button>
          <button className="btn quiet" onClick={() => setDeclining(true)} disabled={busy}>
            Not this time
          </button>
        </div>
      )}

      {confirming && (
        <div className="stack" style={{ gap: 10 }}>
          <p className="notice mint">
            {task.dueDate
              ? `You're taking this on, by ${formatDate(task.dueDate)}.`
              : "You're taking this on, whenever it fits."}
          </p>
          <div className="row">
            <button className="btn mint" onClick={() => act('accept')} disabled={busy}>
              {busy ? 'One moment…' : "I'm in"}
            </button>
            <button className="btn ghost" onClick={() => setConfirming(false)} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {declining && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="field">
            <label htmlFor={`why-${task.id}`}>Anything your coordinator should know? Optional.</label>
            <input
              id={`why-${task.id}`}
              type="text"
              value={note}
              placeholder="Away that weekend"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="row">
            <button className="btn quiet" onClick={() => act('decline', { note })} disabled={busy}>
              Send
            </button>
            <button className="btn ghost" onClick={() => setDeclining(false)} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- declined */}
      {task.myStatus === 'declined' && (
        <div className="stack" style={{ gap: 10 }}>
          <p className="notice marigold">No problem — this one won't be shown to you again.</p>
          <div>
            <button className="btn quiet" onClick={() => act('reconsider')} disabled={busy}>
              I can help after all
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- accepted */}
      {task.myStatus === 'accepted' && !finishing && (
        <div className="row">
          <button className="btn mint" onClick={() => setFinishing(true)} disabled={busy}>
            Mark it done
          </button>
          <button className="btn ghost" onClick={() => act('withdraw')} disabled={busy}>
            Can't make it
          </button>
        </div>
      )}

      {finishing && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="field">
            <label htmlFor={`proof-${task.id}`}>A link or a photo name, if you have one</label>
            <input
              id={`proof-${task.id}`}
              type="text"
              value={proof}
              placeholder="instagram.com/p/…"
              onChange={(e) => setProof(e.target.value)}
            />
            <span className="hint">Optional — it's what your coordinator can point at when the city reports.</span>
          </div>
          <AttachField
            label="Add a photo"
            accept="image/*"
            value={photo}
            onChange={setPhoto}
            hint="Optional — a picture of the thing, done."
          />
          <div className="field">
            <label htmlFor={`note-${task.id}`}>How did it go?</label>
            <AutoTextarea id={`note-${task.id}`} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="row">
            <button
              className="btn mint"
              onClick={() => act('complete', { proof, note, ...(photo ? { proofFileId: photo.id } : {}) })}
              disabled={busy}
            >
              {busy ? 'Saving…' : "That's done"}
            </button>
            <button className="btn ghost" onClick={() => setFinishing(false)} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {task.myStatus === 'completed' && <p className="notice mint">Thank you. Your coordinator can see this.</p>}
    </article>
  );
}

function formatDate(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
